import { createRef, forwardRef, PureComponent } from "react";
import { createPortal } from "react-dom";
import { bool, func, number, node, string } from "prop-types";
import styled from "styled-components/macro";
import { rem, transparentize } from "polished";
import uniqueId from "lodash/uniqueId";

import { BREAKPOINT_WRAPPER } from "../../constants";
import { colors, fonts } from "../../styles/Styles.variables";

import { CSSTransition } from "react-transition-group";

const portalRoot = document.getElementById("portal-root");
const overlayDuration = 300;

class Modal extends PureComponent {
  constructor(props) {
    super(props);

    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleModal = this.handleModal.bind(this);

    this.closeButton = createRef();
    this.nodeRef = createRef();

    // Could be replaced by useId hook when upgraded to react 18+ and if refactored as a functional component
    this.modalUniqueClass = uniqueId("modal-");

    let modalsContainer = document.getElementById("modals-container");
    if (!modalsContainer) {
      modalsContainer = document.createElement("div");
      modalsContainer.setAttribute("id", "modals-container");
      portalRoot.appendChild(modalsContainer);
    }

    this.el = modalsContainer;
  }

  /**
   * Handle keyboard interactions
   * @param {event} e
   * @public
   */
  handleKeyDown(e) {
    return e.keyCode === 27 && this.props.close();
  }

  /**
   * Handle opening or closing modal
   * @param {boolean} forceClose
   * @public
   */
  handleModal(forceClose) {
    const { closeButton, modalUniqueClass } = this;
    const { open, openBodyClass } = this.props;

    if (!open || forceClose) {
      if (document.body.classList.contains(modalUniqueClass)) {
        document.body.classList.remove(openBodyClass, modalUniqueClass);
      }
    } else {
      document.body.classList.add(openBodyClass, modalUniqueClass);
      if (!!closeButton.current) {
        closeButton.current.focus();
      }
    }
  }

  componentDidMount() {
    this.handleModal();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.open !== this.props.open) {
      this.handleModal();
    }
  }

  componentWillUnmount() {
    this.handleModal(true);
  }

  render() {
    const { closeButton, handleKeyDown, nodeRef } = this;
    const {
      ariaLabel,
      children,
      close,
      showClose,
      open,
      size,
      zIndex,
      className,
    } = this.props;
    const closeEventHandlers = !!close
      ? {
          onMouseDown: close,
          onKeyDown: handleKeyDown,
        }
      : {};

    return open && !!this.el
      ? createPortal(
          <CSSTransition
            appear={true}
            in={open}
            nodeRef={nodeRef}
            timeout={overlayDuration}
          >
            <Overlay
              aria-label={ariaLabel}
              zIndex={zIndex}
              {...closeEventHandlers}
              className={!!className ? className : ""}
              ref={nodeRef}
            >
              <Body showClose={showClose} size={size}>
                {showClose && <Close onClick={close} ref={closeButton} />}
                {children}
              </Body>
            </Overlay>
          </CSSTransition>,
          this.el
        )
      : null;
  }
}

Modal.propTypes = {
  ariaLabel: string,
  children: node.isRequired,
  close: func,
  open: bool,
  openBodyClass: string,
  size: string,
  zIndex: number,
  showClose: bool,
};

Modal.defaultProps = {
  ariaLabel: "modal",
  open: false,
  openBodyClass: "no-scroll",
  size: "medium",
  zIndex: 9999999999,
  showClose: true,
};

const Overlay = styled.div.attrs(props => ({
  "aria-modal": true,
  role: "dialog",
  tabIndex: "-1",
}))`
  background-color: ${colors.black};
  height: 100%;
  left: 0;
  opacity: 0;
  position: fixed;
  right: 0;
  top: 0;
  transform: translateZ(0);
  transition: opacity ${overlayDuration}ms;
  width: 100%;
  z-index: ${({ zIndex }) => zIndex};

  @media ${BREAKPOINT_WRAPPER} {
    align-items: center;
    background-color: ${transparentize(0.2, colors.black)};
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  &.enter-active,
  &.enter-done,
  &.exit {
    opacity: 1;
  }

  &.enter,
  &.exit-active {
    opacity: 0;
  }
`;

const bodyPadX = 14;
const bodyPadXRem = rem(bodyPadX + "px");

export const Body = styled.div.attrs(props => ({
  onClick: e => e.stopPropagation(),
  onMouseDown: e => e.stopPropagation(),
  role: "document",
}))`
  background-color: ${colors.black};
  bottom: 0;
  color: ${colors.white};
  display: flex;
  flex-direction: column;
  height: 100%;
  left: 0;
  margin: 0 auto;
  max-width: ${({ size }) => {
    switch (size) {
      case "small":
        return rem("560px");
      case "big":
        return rem("888px");
      default:
        return rem("700px");
    }
  }};
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: ${rem("24px")} ${bodyPadXRem} 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;

  a {
    color: ${colors.yellow};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  @media ${BREAKPOINT_WRAPPER} {
    border-radius: ${rem("6px")};
    bottom: auto;
    display: block;
    height: auto;
    left: auto;
    max-height: 100vh;
    padding: ${({ size }) => {
      switch (size) {
        case "small":
          return `${rem("46px")} ${rem("38px")} ${rem("38px")}`;
        default:
          return `${rem("46px")} ${rem("50px")} ${rem("50px")}`;
      }
    }};
    position: relative;
    right: 0;
    top: 0;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  header {
    &:not(.alert-only) {
      padding-bottom: ${rem("17px")};

      @media ${BREAKPOINT_WRAPPER} {
        padding-bottom: ${rem("24px")};
      }
    }

    &.alert-only {
      padding-top: ${rem("36px")};

      @media ${BREAKPOINT_WRAPPER} {
        padding-top: ${rem("6px")};
      }
    }

    h1 {
      font-size: ${rem("22px")};
      font-weight: ${fonts.weights.normal};
      line-height: 1.2;
      margin-bottom: ${rem("13px")};
      padding-right: ${({ showClose }) => (showClose ? rem("50px") : 0)};

      @media ${BREAKPOINT_WRAPPER} {
        font-size: ${rem("28px")};
        padding-right: 0;
      }
    }

    p {
      font-size: ${rem("14px")};
      line-height: 1.2;

      @media ${BREAKPOINT_WRAPPER} {
        font-size: ${rem("16px")};
      }

      &.mb-0 {
        margin-bottom: 0;
      }
    }
  }

  form,
  .modal-body {
    display: flex;
    flex-direction: column;
    flex-grow: 2;
    justify-content: space-between;

    @media ${BREAKPOINT_WRAPPER} {
      display: block;
    }
  }

  .form-actions,
  .modal-actions {
    margin: auto -${bodyPadXRem} 0;
    padding-top: ${bodyPadXRem};
    width: calc(100% + ${rem(bodyPadX * 2 + "px")});

    @media ${BREAKPOINT_WRAPPER} {
      margin: 0;
      width: 100%;
    }

    button {
      height: ${rem("54px")};

      @media ${BREAKPOINT_WRAPPER} {
        height: auto;
      }
    }
  }

  .form-disclaimer {
    font-size: 80%;
    font-style: italic;
    padding-bottom: ${rem("20px")};
    padding-top: ${rem("20px")};

    a {
      color: ${colors.yellow};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const Close = styled(
  forwardRef((props, ref) => (
    <button ref={ref} {...props}>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
        <path
          d="M19.18 17.59L11.58 10l7.59-7.59a1.11 1.11 0 0 0 .33-.79 1.15 1.15 0 0 0-.32-.79 1.13 1.13 0 0 0-1.59 0L10 8.42 2.41.83A1.11 1.11 0 0 0 1.62.5a1.15 1.15 0 0 0-.79.32 1.13 1.13 0 0 0 0 1.59L8.42 10 .83 17.59a1.12 1.12 0 0 0 .78 1.91 1.12 1.12 0 0 0 .79-.32l7.6-7.6 7.59 7.59a1.11 1.11 0 0 0 .79.33 1.11 1.11 0 0 0 .79-.33 1.09 1.09 0 0 0 .33-.78 1.14 1.14 0 0 0-.32-.8z"
          fill="currentColor"
        />
      </svg>
    </button>
  ))
).attrs(props => ({
  "aria-label": "close",
  type: "button",
}))`
  appearance: none;
  background: none;
  border: 0 none;
  color: inherit;
  height: ${rem("20px")};
  margin: 0;
  padding: 0;
  position: absolute;
  right: ${rem("22px")};
  top: ${rem("21px")};
  user-select: none;
  width: ${rem("20px")};

  &:focus {
    outline: ${rem("1px")} dotted;
    outline-offset: ${rem("2px")};
  }

  svg {
    display: block;
    height: ${rem("20px")};
    width: ${rem("20px")};
  }
`;

export default Modal;
